import { BNBox, resolveUrl } from "@bn-monorepo/js-packages";
import { BoxProps, Stack, useTheme } from "@mui/material";
import { BNHeaderMegaMenuLink } from "./BNHeaderMegaMenuLink";
import { MegaMenu } from "../../CmsContent/services";
import { useContext } from "react";
import { SettingsContext } from "../../../settingsContext";

interface Props {
  list: MegaMenu["list"];
  sx?: BoxProps["sx"];
}

export const BNHeaderMegaMenuDesktop: React.FC<Props> = ({ list, sx }) => {
  const theme = useTheme();
  const { env } = useContext(SettingsContext);
  return (
    <BNBox
      component={"nav"}
      data-uit-container={"top-navigation"}
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        borderBottom: `0.1rem solid ${theme.vars.palette.secondary.light}`,
        ...sx,
      }}
    >
      {list
        ?.filter((level1) => !level1.hidden.desktop)
        .map((level1) => {
          if (level1.list.length) {
            return (
              <BNBox
                key={level1.link.url}
                sx={{
                  position: "relative",
                  "& > .mega-menu-visibility,&:not(:focus-within) .mega-menu-visibility.backdrop:hover": {
                    display: "none",
                    opacity: 0,
                  },
                  "&:has(> :hover) > .mega-menu-visibility, &:focus-within > .mega-menu-visibility": {
                    display: "block",
                    opacity: 1,
                  },
                }}
              >
                <BNBox
                  sx={{
                    p: "0.1rem", // To see outline on tabbing
                    height: "100%",
                  }}
                >
                  <BNHeaderMegaMenuLink
                    level={1}
                    to={resolveUrl(level1.link.url)}
                    key={level1.link.url}
                    sx={{ height: "100%" }}
                    showArrowForward
                    iconDirection="down"
                    data-cms-target-key={env !== "prod" ? level1.link.udi : undefined}
                  >
                    {level1.link.name}
                  </BNHeaderMegaMenuLink>
                </BNBox>
                <BNBox
                  className="mega-menu-visibility"
                  sx={{
                    position: "absolute",
                    // Need to have it 0.1 rem smaller with a borderTop 0.1rem to not loose the hover when moving the mouse down
                    top: "5.9rem",
                    borderTop: `0.1rem solid ${theme.vars.palette.secondary.light}`,
                    maxHeight: `calc(100vh - 5.9rem)`,
                    overflowY: "auto",
                    zIndex: 1601,
                  }}
                >
                  <BNBox
                    component={Stack}
                    sx={{
                      p: 4,
                      backgroundColor: "background.paper",
                      width: "32rem",
                      mx: "auto",
                      //To keep the .mega-menu-content:hover
                      mt: "-0.1rem",
                      gap: "0.2rem",
                      borderBottom: `0.1rem solid ${theme.vars.palette.secondary.light}`,
                      borderBottomLeftRadius: theme.shape.borderRadius * 8,
                      borderBottomRightRadius: theme.shape.borderRadius * 8,
                      "& > *:first-of-type": {
                        borderTopLeftRadius: theme.shape.borderRadius * 2,
                        borderTopRightRadius: theme.shape.borderRadius * 2,
                      },
                      "& > *:last-of-type": {
                        borderBottomLeftRadius: theme.shape.borderRadius * 2,
                        borderBottomRightRadius: theme.shape.borderRadius * 2,
                      },
                    }}
                  >
                    {level1.list
                      .filter((level2) => !level2.hidden.desktop)
                      .map((level2) => (
                        <BNHeaderMegaMenuLink
                          level={1}
                          to={resolveUrl(level2.link.url)}
                          key={level2.link.url}
                          iconInlineSvg={level2.iconInlineSvg}
                          data-cms-target-key={env !== "prod" ? level2.link.udi : undefined}
                          showArrowForward={false}
                        >
                          {level2.link.name}
                        </BNHeaderMegaMenuLink>
                      ))}
                  </BNBox>
                </BNBox>
                <BNBox
                  className="mega-menu-visibility backdrop"
                  sx={{
                    position: "fixed",
                    top: "6rem",
                    left: 0,
                    width: `calc(100%)`,
                    height: `calc(100vh - 6rem)`,

                    overflowY: "auto",
                    zIndex: 1600,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                  }}
                />
              </BNBox>
            );
          }

          return (
            level1.link && (
              <BNHeaderMegaMenuLink
                level={1}
                to={resolveUrl(level1.link.url)}
                key={level1.link.url}
                showArrowForward={false}
                data-cms-target-key={env !== "prod" ? level1.link.udi : undefined}
              >
                {level1.link.name}
              </BNHeaderMegaMenuLink>
            )
          );
        })}
    </BNBox>
  );
};
